import React, { Suspense } from 'react';
import {
  Box, Paper, styled, Typography, Skeleton, IconButton, Tooltip
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { ContentCopy, Check } from '@mui/icons-material';
import copy from 'copy-to-clipboard';
import { AIanswer } from './types';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

const QusetionTypography = styled(Typography)(() => ({
  backgroundColor: grey[100],
}));

// 定义动画
const animations = `
  @keyframes blink {
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
  }
  @keyframes typing {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
`;

// 处理 LightRAG 的文档引用标记
function processDocumentReferences(text: string): string {
  if (!text) return text;

  let processed = text;

  // 1. 统一清理极其不规范的标记
  // 清理 <ref>...</ref> 标签，只保留内部内容
  processed = processed.replace(/<\/?ref\s*>/gi, '');

  // 2. 清理流式传输中可能被打断的 <sup> 标签碎片
  // 或者是已经存在的 <sup> 标签，统一交由 renderInlineMarkdown 处理 [n] 格式
  processed = processed.replace(/<\/?sup\s*>/gi, '');

  // 3. 处理 ^[[数字]] 或 ^[数字] 或 [[数字]] 这种常见的 AI 引用格式异常
  processed = processed.replace(/\^?\[+?(\d+)\]+/g, '[$1]');

  // 4. 处理 <SEP>文档[数字] -> 直接转为 [数字]
  processed = processed.replace(/<SEP>文档\s*\[(\d+)\]/g, '[$1]');

  // 5. 处理 [数字]> 这种残余尖括号的情况 -> 直接转为 [数字]
  processed = processed.replace(/\[(\d+)\]>/g, '[$1]');

  return processed;
}

// 仅解析行内的图片、加粗和上角标
function renderInlineMarkdown(text: string) {
  if (!text) return [{ type: 'text' as const, content: text }];

  const parts: Array<{ type: 'text' | 'image' | 'sup' | 'bold' | 'br', content?: string, src?: string, alt?: string }> = [];

  // 匹配：
  // 1. 图片: ![alt](url)
  // 2. 加粗: **text**
  // 3. 引用标记: [数字]
  // 4. 换行符: <br>
  const combinedRegex = /!\[(.*?)\]\((.*?)\)|\*\*(.*?)\*\*|\[(\d+)\](?![0-9.])|<br\s*\/?>/gi;

  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }

    if (match[1] !== undefined) {
      parts.push({ type: 'image', alt: match[1], src: match[2] });
    } else if (match[3] !== undefined) {
      parts.push({ type: 'bold', content: match[3] });
    } else if (match[4] !== undefined) {
      parts.push({ type: 'sup', content: match[4] });
    } else if (match[0].toLowerCase().startsWith('<br')) {
      parts.push({ type: 'br' });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return parts;
}

interface RelatedFilesProps {
  tid?: string;
  references: AIanswer['references'];
}

export function RelatedFiles(props: RelatedFilesProps) {
  const { references } = props;

  // 处理LightRAG格式的references
  if (references.length > 0 && references[0].references && Array.isArray(references[0].references)) {
    const referenceData = references[0].references;
    if (referenceData.length === 0) return null;

    return (
      <Box mt={2}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          参考文档:
        </Typography>
        {referenceData.map((ref: { reference_id: string, file_path: string }, index: number) => (
          <Box key={ref.reference_id || index} sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • {ref.file_path}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  // 处理传统AI格式的references
  if (references.length === 0 || !references[0].data || references[0].data.length === 0) {
    return null;
  }

  const reference = references[0];
  const referenceData = reference.data;
  const astId = [];

  if (referenceData && referenceData.length > 0) {
    for (const item of referenceData) {
      astId.push(item.fid);
    }
  }

  // 这里需要外部注入获取文档数据的逻辑
  // 暂时返回null，具体实现由使用方提供
  return null;
}

interface QATextItemProps {
  tid: string;
  item: {
    type?: 'text';
    id: number;
    question: string;
    answer: string;
    done: boolean;
  };
  onClickAsset?: () => void;
  onScrollToBottom?: () => void;
}

export function QATextItem(props: QATextItemProps) {
  const { item, onScrollToBottom } = props;
  const { question, answer, done } = item;
  const [copied, setCopied] = React.useState(false);

  // 通知外层检查是否需要滚动到底部（外层会根据autoScrollEnabled状态决定是否滚动）
  React.useEffect(() => {
    if (onScrollToBottom && answer && !done) {
      onScrollToBottom();
    }
  }, [answer, done, onScrollToBottom]);

  const handleCopy = (text: string) => {
    if (!text) return;
    const success = copy(text);
    if (success) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatAnswer: AIanswer = {
    message: [],
    references: [],
    end: []
  };

  function safeParse(jsonString: string) {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return '';
    }
  }

  function processEventChunk(chunk: string) {
    const event = {
      type: 'message',
      data: '',
      id: null,
      retry: null
    };

    chunk.split('\n').forEach(line => {
      if (line.startsWith('event:')) {
        event.type = line.replace('event:', '').trim();
      } else if (line.startsWith('data:')) {
        event.data += line.replace('data:', '').trim() + '\n';
      } else if (line.startsWith('id:')) {
        event.id = line.replace('id:', '').trim();
      } else if (line.startsWith('retry:')) {
        event.retry = parseInt(line.replace('retry:', '').trim(), 10);
      }
    });

    const trimData = event.data.trim();
    const jsonData = safeParse(trimData);

    return {
      type: event.type,
      data: {
        id: event.id,
        retry: event.retry,
        data: jsonData,
      }
    };
  }

  if (answer && answer.length !== 0) {
    const chunks = answer.split(/\n\n/).filter(chunk => chunk.trim());
    for (const chunk of chunks) {
      const { type, data } = processEventChunk(chunk);
      // 确保数组存在
      if (!formatAnswer[type]) {
        formatAnswer[type] = [];
      }
      formatAnswer[type].push(data);
    }
  }

  // 检查是否有 end 事件且包含 content（可能是 precise_ai 或普通 AI 的最终回答）
  const hasEndContent = (formatAnswer.end || []).length > 0 && (formatAnswer.end[0]?.data as { content?: string })?.content;

  // 检查是否是 precise_ai 模式（通过是否存在 thinking 事件判断）
  const isPreciseAI = (formatAnswer.thinking || []).length > 0;

  // 处理 thinking 事件（工具调用过程）
  const thinkingContent = (formatAnswer.thinking || []).map((item: { role?: string, content?: string, tool_calls?: { id?: string, type?: string }[] }) => {
    if (item.tool_calls && item.tool_calls.length > 0) {
      return '正在查询相关信息...';
    }
    return item.content || '';
  }).filter(Boolean).join('\n');

  // 根据响应内容获取答案
  let messages = '';

  // 优先检查 end 事件的 content（适用于有最终回答的情况）
  if (hasEndContent) {
    messages = (formatAnswer.end[0]?.data as { content?: string })?.content || '';
  } else if (isPreciseAI) {
    // Precise AI 但没有 end content，尝试其他位置
    const endData = (formatAnswer.end || []).length > 0 ? formatAnswer.end[0]?.data : null;
    messages = (endData as { content?: string })?.content || '';
    if (!messages) {
      // fallback 到 message 内容
      messages = (formatAnswer.message || []).map((msg: { data?: { d?: string, content?: string } }) => msg.data?.d || '').join('');
    }
  } else {
    // 普通 AI: 答案在 message 事件中
    messages = (formatAnswer.message || []).map((msg: { data?: { d?: string, content?: string } }) => {
      const content = msg.data?.d || msg.data?.content || '';
      return content;
    }).join('');
  }

  // 处理 LightRAG 的文档引用标记
  messages = processDocumentReferences(messages);

  // 检查是否有引用（支持不同的引用格式）
  const hasReferences = (formatAnswer.references || []).length > 0 && (
    (formatAnswer.references[0]?.data && Array.isArray(formatAnswer.references[0]?.data) && formatAnswer.references[0]?.data.length > 0) ||
    (formatAnswer.references[0]?.references && Array.isArray(formatAnswer.references[0]?.references) && formatAnswer.references[0]?.references.length > 0)
  );

  // 为复制准备纯文本（去掉 HTML 标签）
  const getPlainText = (htmlText: string) => {
    return htmlText
    .replace(/<sup\s*>[\s\n]*\[?(\d+)\]?[\s\n]*<\/sup\s*>/gi, '[$1]')
    .replace(/<[^>]+>/g, ''); // 移除所有 HTML 标签
  };

  // 核心优化：将文本划分为不同的块（表格、引用、普通行、分割线）
  const parseBlocks = (text: string) => {
    const lines = text.split('\n');
    const blocks: any[] = [];
    let currentTable: any = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // 1. 处理表格
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const cells = line.split('|').map(c => c.trim()).filter((c, idx, arr) => idx > 0 && idx < arr.length - 1);
        const isSeparator = cells.length > 0 && cells.every(c => /^[ :-|-]+$/.test(c));

        if (currentTable) {
          if (!isSeparator) currentTable.rows.push(cells);
        } else if (!isSeparator) {
          currentTable = { type: 'table', rows: [cells] };
          blocks.push(currentTable);
        }
        continue;
      }
      currentTable = null; // 结束表格连贯性

      // 2. 处理分割线
      if (trimmed === '---' || trimmed === '***') {
        blocks.push({ type: 'hr' });
        continue;
      }

      // 3. 处理引用块
      if (trimmed.startsWith('>')) {
        blocks.push({ type: 'blockquote', content: trimmed.replace(/^>[\s]*/, '') });
        continue;
      }

      // 4. 普通行 (包含已处理的标题、列表项逻辑)
      blocks.push({ type: 'line', content: line });
    }
    return blocks;
  };

  return (
    <Box mb={3}>
      {/* 注入动画 */}
      <style>{animations}</style>

      <Box display='flex' justifyContent='flex-end' mb={1}>
        <QusetionTypography
          variant='body1'
          sx={{
            p: 1.5,
            borderRadius: 2,
            maxWidth: '80%',
            wordWrap: 'break-word'
          }}
        >
          {question}
        </QusetionTypography>
      </Box>

      {/* 显示思考过程（仅在 precise_ai 模式下） */}
      {thinkingContent && (
        <Box display='flex' justifyContent='flex-start' mb={1}>
          <Paper
            elevation={1}
            sx={{
              p: 1.5,
              borderRadius: 2,
              maxWidth: '80%',
              bgcolor: 'grey.100',
              color: 'text.secondary'
            }}
          >
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
              {thinkingContent}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* 显示答案 - 直接显示正文（打字机效果） */}
      <Box display='flex' justifyContent='flex-start' mb={1}>
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            borderRadius: 2,
            maxWidth: '100%', // 允许表格等内容占满宽度
            bgcolor: 'transparent',
            color: 'text.primary'
          }}
        >
          <PhotoProvider>
            <Box>
              {/* 加载指示器：当还没有收到内容且未完成时显示骨架屏 */}
              {!done && !messages && !thinkingContent && (
                <Box sx={{ py: 0.5, minWidth: '160px' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center' }}>
                    正在思考中
                    <Box component="span" sx={{
                      display: 'inline-flex',
                      ml: 0.5,
                      '& span': {
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: 'text.secondary',
                        mx: 0.2,
                        animation: 'typing 1.4s infinite both',
                        '&:nth-of-type(2)': { animationDelay: '0.2s' },
                        '&:nth-of-type(3)': { animationDelay: '0.4s' }
                      }
                    }}>
                      <span /><span /><span />
                    </Box>
                  </Typography>
                  <Skeleton animation="wave" height={20} width="100%" sx={{ mb: 0.5, borderRadius: 1 }} />
                  <Skeleton animation="wave" height={20} width="90%" sx={{ mb: 0.5, borderRadius: 1 }} />
                  <Skeleton animation="wave" height={20} width="60%" sx={{ borderRadius: 1 }} />
                </Box>
              )}

              {/* 解析块级内容（表格、引用、普通行等） */}
              {parseBlocks(messages).map((block: any, bIdx: number) => {
                // 1. 渲染表格
                if (block.type === 'table') {
                  return (
                    <Box key={bIdx} sx={{ overflowX: 'auto', my: 2, border: '1px solid #eee', borderRadius: 1 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            {block.rows[0].map((cell: string, cIdx: number) => (
                              <th key={cIdx} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #eee', fontWeight: 'bold' }}>
                                {renderInlineMarkdown(cell).map((part, pIdx) => (
                                  <Typography key={pIdx} component='span' variant='inherit' sx={{ fontWeight: 'inherit' }}>
                                    {part.content}
                                  </Typography>
                                ))}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows.slice(1).map((row: string[], rIdx: number) => (
                            <tr key={rIdx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              {row.map((cell, cIdx) => (
                                <td key={cIdx} style={{ padding: '8px 12px', verticalAlign: 'top' }}>
                                  {renderInlineMarkdown(cell).map((part, pIdx) => (
                                    <Typography key={pIdx} component='span' variant='inherit' sx={{ fontWeight: part.type === 'bold' ? 'bold' : 'inherit' }}>
                                      {part.content}
                                    </Typography>
                                  ))}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  );
                }

                // 2. 渲染引用块
                if (block.type === 'blockquote') {
                  return (
                    <Box key={bIdx} sx={{ pl: 2, py: 0.5, my: 1, borderLeft: '4px solid', borderColor: grey[300], bgcolor: grey[50], color: 'text.secondary', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      {renderInlineMarkdown(block.content).map((part, pIdx) => (
                        <Typography key={pIdx} component='span' variant='inherit' sx={{ fontWeight: part.type === 'bold' ? 'bold' : 'inherit' }}>
                          {part.content}
                        </Typography>
                      ))}
                    </Box>
                  );
                }

                // 3. 渲染分割线
                if (block.type === 'hr') {
                  return <Box key={bIdx} component='hr' sx={{ border: 'none', borderTop: '1px solid', borderColor: grey[200], my: 2 }} />;
                }

                // 4. 渲染普通行
                const line = block.content;
                if (line.trim() === '') {
                  return <Box key={bIdx} sx={{ height: '8px' }} />;
                }

                // 判断是否是标题
                const headerMatch = line.match(/^(#{1,6})\s+(.*)/);
                const isHeader = !!headerMatch;
                // 判断是否是列表项
                const isListItem = line.trim().startsWith('- ') || line.trim().startsWith('* ');

                let cleanLine = line;
                if (isHeader) {
                  cleanLine = headerMatch[2];
                } else if (isListItem) {
                  cleanLine = line.trim().replace(/^[-*]\s+/, '');
                }

                return (
                  <Box
                    key={bIdx}
                    sx={{
                      mb: isListItem ? 0.3 : 0.5,
                      pl: isListItem ? 2.5 : 0, // 列表项增加缩进
                      position: 'relative',
                      lineHeight: 1.6,
                      fontWeight: isHeader ? 'bold' : 'normal',
                      fontSize: isHeader ? (headerMatch[1].length <= 2 ? '1.15rem' : '1.05rem') : '0.925rem', // 根据级别调整标题大小
                      color: isHeader ? 'text.primary' : 'text.secondary', // 颜色区分
                      display: 'block'
                    }}
                  >
                    {/* 列表圆点渲染 */}
                    {isListItem && (
                      <Box
                        component='span'
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '0.65em',
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          bgcolor: 'currentColor',
                          opacity: 0.8
                        }}
                      />
                    )}
                    {renderInlineMarkdown(cleanLine).map((part, partIdx) => {
                      if (part.type === 'image') {
                        return (
                          <Box key={partIdx} component="span" sx={{ display: 'block', my: 1 }}>
                            <PhotoView src={part.src}>
                              <img src={part.src} alt={part.alt} style={{ maxWidth: '100%', borderRadius: '4px', cursor: 'pointer' }} />
                            </PhotoView>
                          </Box>
                        );
                      }
                      if (part.type === 'br') {
                        return <Box key={partIdx} component='br' sx={{ display: 'block', content: '""', mt: 0.5 }} />;
                      }
                      if (part.type === 'sup') {
                        return (
                          <Typography
                            key={partIdx}
                            component='sup'
                            variant='caption'
                            sx={{ color: 'primary.main', fontWeight: 'bold', verticalAlign: 'super', ml: 0.2 }}
                          >
                            [{part.content}]
                          </Typography>
                        );
                      }
                      if (part.type === 'bold') {
                        return (
                          <Typography key={partIdx} component='span' variant='body1' sx={{ fontWeight: 'bold' }}>
                            {part.content}
                          </Typography>
                        );
                      }
                      return (
                        <Typography key={partIdx} component='span' variant='body1' sx={{ whiteSpace: 'pre-wrap', fontWeight: 'inherit' }}>
                          {part.content}
                        </Typography>
                      );
                    })}
                  </Box>
                );
              })}

              {/* 加载指示器：仅在未完成且有内容时显示光标 */}
              {!done && messages && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '1.1em',
                    bgcolor: grey[700], // 使用 MUI grey
                    ml: 0.5,
                    verticalAlign: 'text-bottom',
                    animation: 'blink 1s infinite',
                  }}
                />
              )}
            </Box>
          </PhotoProvider>

          {/* 复制按钮 - 仅在回答完毕后显示 */}
          {done && messages && (
            <Box display='flex' justifyContent='flex-end' mt={0.5}>
              <Tooltip title={copied ? '已复制' : '复制答案'} placement='top'>
                <IconButton
                  size='small'
                  onClick={() => handleCopy(getPlainText(messages))}
                  sx={{
                    color: grey[500],
                    '&:hover': { color: 'primary.main' },
                    p: 0.5
                  }}
                >
                  {copied ? <Check fontSize='small' color='success' /> : <ContentCopy sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Paper>
      </Box>

      {hasReferences && (
        <Suspense fallback={<div>Loading...</div>}>
          <RelatedFiles
            references={
              formatAnswer.references[0]?.references
                ? [{ id: '', retry: '', references: formatAnswer.references[0].references }]
                : formatAnswer.references
            }
          />
        </Suspense>
      )}
    </Box>
  );
}
